/**
 * Registration payment-option visibility helpers.
 *
 * Mirrors the backend resolver in
 * api_backend_fastify_server/src/domain/registration-payment-options.ts, but
 * operates on the already-resolved `/registration-policy` response rather than
 * raw columns — the server does the org -> rodeo -> event inheritance and hands
 * the client the answers.
 *
 * The server is the authority: it rejects a disallowed method with a 403
 * (PAY_LATER_NOT_ALLOWED / MEMBERS_ONLY_PAYMENT_METHOD). Everything here is
 * about not showing a contestant an option they can't have.
 */

export interface RegistrationPolicyLike {
  is_member: boolean;
  member_fee_enabled: boolean;
  allow_cash_payment?: boolean;
  allow_pay_later?: boolean;
}

export interface RegistrationPaymentVisibility {
  /** Render the Pay Later card at all? */
  payLaterAllowed: boolean;
  /** Render the Cash at Event card at all? */
  cashAllowed: boolean;
  /** Does this rodeo require org membership? */
  membershipRequired: boolean;
  /**
   * Deferred options (Pay Later / Cash) are visible but locked for this user.
   * Only ever true when membership is required AND the user isn't a member —
   * on an open rodeo nothing is locked.
   */
  deferredMembersOnly: boolean;
  /** Render the three-path membership card (pay non-member fee / claim / join)? */
  showMembershipCard: boolean;
}

/**
 * Derive what the registration payment step should show.
 *
 * A null policy (still loading, or an unauthenticated visitor) falls back to
 * the historical defaults: Pay Later on, cash off, nothing locked, no
 * membership card — so the UI never flashes an option away and back.
 */
export function resolveRegistrationPaymentVisibility(
  policy: RegistrationPolicyLike | null | undefined,
): RegistrationPaymentVisibility {
  // `allow_pay_later` defaults to true because Pay Later has always been
  // offered; a payload that predates the field must not hide it.
  const payLaterAllowed = policy?.allow_pay_later ?? true;
  const cashAllowed = policy?.allow_cash_payment ?? false;
  const membershipRequired = policy?.member_fee_enabled === true;
  // When membership isn't required the server reports every entrant as a
  // member, so this collapses to false without a special case.
  const deferredMembersOnly = !!policy && membershipRequired && !policy.is_member;
  return {
    payLaterAllowed,
    cashAllowed,
    membershipRequired,
    deferredMembersOnly,
    showMembershipCard: deferredMembersOnly,
  };
}
